import React, { useState } from 'react';

import type { Category, Subcategory } from 'hooks/useDirectoryData/types';

import clsx from 'clsx';
import i18n from 'i18n-react';

interface CategoryFilterProps {
  categories: Category[];
  selectedCategory: Category | null;
  selectedSubcategory: Subcategory | null;
  filterOpen: boolean;
  onCategorySelect: (category: Category | null) => void;
  onSubcategorySelect: (subcategory: Subcategory | null) => void;
  onSubmit?: (category: Category | null, subcategory: Subcategory | null) => void;
  onResetFilters: () => void;
  onToggleFilter: () => void;
  variant?: 'checkbox' | 'inline' | 'dropdown';
}

function CategoryFilter({
  categories,
  selectedCategory,
  selectedSubcategory,
  filterOpen,
  onCategorySelect,
  onSubcategorySelect,
  onSubmit,
  onResetFilters,
  onToggleFilter,
  variant = 'checkbox',
}: CategoryFilterProps) {
  const [pendingCategory, setPendingCategory] = useState<Category | null>(selectedCategory);
  const [pendingSubcategory, setPendingSubcategory] = useState<Subcategory | null>(selectedSubcategory);
  const [prevFilterOpen, setPrevFilterOpen] = useState(filterOpen);

  if (variant === 'dropdown' && filterOpen && !prevFilterOpen) {
    setPrevFilterOpen(true);
    setPendingCategory(selectedCategory);
    setPendingSubcategory(selectedSubcategory);
  } else if (!filterOpen && prevFilterOpen) {
    setPrevFilterOpen(false);
  }

  if (variant === 'dropdown') {
    const subcategories = selectedCategory?.subcategories ?? [];

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const cat = categories.find((c) => c.cat_name === e.target.value) ?? null;
      onCategorySelect(cat);
    };

    const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const sub = subcategories.find((s) => s.sub_catname === e.target.value) ?? null;
      onSubcategorySelect(sub);
    };

    return (
      <div className={clsx('filterContainer', 'dropdown', { active: filterOpen })}>
        <select className="filter-select" value={selectedCategory?.cat_name ?? ''} onChange={handleCategoryChange}>
          <option value="" disabled>
            {i18n.translate('directoryView.category') as string}
          </option>
          {categories.map((cat) => (
            <option key={cat.cat_name} value={cat.cat_name}>
              {cat.cat_name_translated}
            </option>
          ))}
        </select>

        {subcategories.length > 0 && (
          <select
            className="filter-select"
            value={selectedSubcategory?.sub_catname ?? ''}
            onChange={handleSubcategoryChange}
          >
            <option value="" disabled>
              {i18n.translate('directoryView.subcategory') as string}
            </option>
            {subcategories.map((sub) => (
              <option key={sub.sub_catname} value={sub.sub_catname}>
                {sub.sub_catname_translated}
              </option>
            ))}
          </select>
        )}

        <div className="resetFilters" onClick={onResetFilters}>
          {i18n.translate('directoryView.resetFilters')}
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    const pendingSubcategories = pendingCategory?.subcategories ?? [];

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const cat = categories.find((c) => c.cat_name === e.target.value) ?? null;
      setPendingCategory(cat);
      setPendingSubcategory(null);
    };

    const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const sub = pendingSubcategories.find((s) => s.sub_catname === e.target.value) ?? null;
      setPendingSubcategory(sub);
    };

    const handleSubmit = () => {
      const sub = pendingCategory ? pendingSubcategory : null;
      if (onSubmit) {
        onSubmit(pendingCategory, sub);
      } else {
        onCategorySelect(pendingCategory);
        onSubcategorySelect(sub);
      }
    };

    const handleClear = () => {
      setPendingCategory(null);
      setPendingSubcategory(null);
      onResetFilters();
      onToggleFilter();
    };

    return (
      <div className={clsx('filterContainer', 'inline', { active: filterOpen })}>
        <div className="dropdown-inner">
          <select className="dropdown-select" value={pendingCategory?.cat_name ?? ''} onChange={handleCategoryChange}>
            <option value="" disabled>
              {i18n.translate('directoryView.category') as string}
            </option>
            {categories.map((cat) => (
              <option key={cat.cat_name} value={cat.cat_name}>
                {cat.cat_name_translated}
              </option>
            ))}
          </select>

          {pendingSubcategories.length > 0 && (
            <select
              className="dropdown-select"
              value={pendingSubcategory?.sub_catname ?? ''}
              onChange={handleSubcategoryChange}
            >
              <option value="" disabled>
                {i18n.translate('directoryView.subcategory') as string}
              </option>
              {pendingSubcategories.map((sub) => (
                <option key={sub.sub_catname} value={sub.sub_catname}>
                  {sub.sub_catname_translated}
                </option>
              ))}
            </select>
          )}

          <div className="dropdown-actions">
            <button className="show-results" onClick={handleSubmit}>
              {i18n.translate('directoryView.showResults') as string}
            </button>
            <button className="clear-btn" onClick={handleClear}>
              {i18n.translate('directoryView.clear') as string}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleCategoryClick = (category: Category) => {
    onCategorySelect(selectedCategory?.cat_name === category.cat_name ? null : category);
  };

  const handleSubcategoryClick = (subcategory: Subcategory) => {
    onSubcategorySelect(selectedSubcategory?.sub_catname === subcategory.sub_catname ? null : subcategory);
  };

  return (
    <div className={clsx('filterContainer', { active: filterOpen })}>
      <div className="checkboxOptions">
        <span className="title">{i18n.translate('directoryView.filters') as string}</span>

        {categories.map((category) => (
          <div key={category.cat_name} className="checkboxDiv">
            <label className="checkbox-label">
              <span className="labelName">{category.cat_name_translated}</span>
              <input
                type="checkbox"
                checked={selectedCategory?.cat_name === category.cat_name}
                onChange={() => {
                  handleCategoryClick(category);
                }}
              />
              <span className="checkmark" />
            </label>

            {selectedCategory?.cat_name === category.cat_name && category.subcategories.length > 0 && (
              <div className="subcategory-options">
                {category.subcategories.map((sub) => (
                  <div key={sub.sub_catname} className="checkboxDiv subcategoryDiv">
                    <label className="checkbox-label">
                      <span className="labelName">{sub.sub_catname_translated}</span>
                      <input
                        type="checkbox"
                        checked={selectedSubcategory?.sub_catname === sub.sub_catname}
                        onChange={() => {
                          handleSubcategoryClick(sub);
                        }}
                      />
                      <span className="checkmark" />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="resetFilters" onClick={onResetFilters}>
        {i18n.translate('directoryView.resetFilters')}
      </div>

      <div className="text-center">
        <button onClick={onResetFilters} className="filter-btn inverted">
          {i18n.translate('directoryView.resetFilters')}
        </button>
        <button className="show-results" onClick={onToggleFilter}>
          {i18n.translate('directoryView.showResults')}
        </button>
      </div>
    </div>
  );
}

export default CategoryFilter;
